import styles from './WalletWidget.module.scss';
import cn from 'classnames';
import parse from 'html-react-parser';
import { Title3, P1 } from "@components/new/Typo";
import { HoverCard } from '@components/new/HoverCard';
import { Button, ButtonType,ButtonSize } from '@components/new/Button';
import CompletionModalWindow from '@components/modals/CompletionModalWindow';
import TopUpBalanceFormModalWindow from '@components/modals/TopUpBalanceFormModalWindow';
import WalletIcon from '@assets/icons/outline/wallet.svg?react';
import { useNavigate } from 'react-router-dom';
import {
  checkBalanceReplenishment,
  checkBalanceReplenishmentResponse,
  checkPaymentLinkStatus
} from '@api/apiClient/finance';
import { createSumFromObject } from '@utils/formatValues';
import {
  getAutoResaleReturnsMissingAmount,
  getAutoResaleReturnsData,
  TAmountWithCurrency,
  TAutoResaleReturnsMissingAmountResponse
} from '@api/apiClient/returns';
import Skeleton from "@components/new/Skeleton";
import { Modal } from '@components/Modal';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useProfile } from '@contexts/ProfileContext';
import { useSellersCounter } from '@contexts/SellersCounterContext';
import { useTranslation } from 'react-i18next';

const WalletWidget = () => {
  const [autoResaleMissingAmount, setIsAutoResaleMissingAmount] = useState<TAmountWithCurrency | undefined>(undefined);
  const [autoResaleMissingAmountValue, setIsAutoResaleMissingAmountValue] = useState<number>(0);
  const [balanceReplenishmentError, setBalanceReplenishmentError] = useState<string | undefined>(undefined);
  const [balanceReplenishmentInProcessed, setBalanceReplenishmentInProcessed] = useState<boolean>(false);
  const [isAutoResaleEnabled, setIsAutoResaleEnabled] = useState<boolean>(false);
  const [isOpenCompletionWindow, setIsOpenCompletionWindow] = useState<boolean>(false);
  const [isOpenTopUpBalanceFormWindow, setIsOpenTopUpBalanceFormWindow] = useState<boolean>(false);

  const { account, get } = useProfile();
  const { sellersNotFound } = useSellersCounter();

  const timeoutCheckAutoResaleMissingAmount = useRef<number | null>(null);
  const timeoutCheckBalance = useRef<number | null>(null);
  const timeoutCheckLink = useRef<number | null>(null);
  const timeoutCheckQr = useRef<number | null>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: dataAutoResaleData, isLoading: isLoadingAutoResaleData } = useQuery({
    queryKey: ['getAutoResaleReturnsData'],
    queryFn: getAutoResaleReturnsData,
    retry: 1,
    enabled: account?.isTestUser && account?.features.customerReturnsResales && !sellersNotFound
  });

  const {
    data: dataAutoResaleMissingAmount,
    refetch: refetchAutoResaleMissingAmount,
    isLoading: isLoadingAutoResaleMissingAmount,
  } = useQuery({
    queryKey: ['getAutoResaleReturnsMissingAmount'],
    queryFn: getAutoResaleReturnsMissingAmount,
    retry: 1,
    enabled: account?.isTestUser && account?.features.customerReturnsResales && !sellersNotFound
  });

  const { mutate: mutateCheckBalanceReplenishment } = useMutation({
    mutationFn: checkBalanceReplenishment
  });

  const { mutate: mutateCheckPaymentLinkStatus } = useMutation({
    mutationFn: checkPaymentLinkStatus
  });

  const { mutate: mutateGetAutoResaleReturnsMissingAmount } = useMutation({
    mutationFn: getAutoResaleReturnsMissingAmount
  });

  useEffect(() => {
    if (dataAutoResaleData && dataAutoResaleMissingAmount) {
      const { amount } = dataAutoResaleMissingAmount;
      const { enabled } = dataAutoResaleData;
      setIsAutoResaleMissingAmount(amount);
      setIsAutoResaleEnabled(enabled);
    }
  }, [dataAutoResaleData, dataAutoResaleMissingAmount]);

  useEffect(() => {
    if (autoResaleMissingAmount && !autoResaleMissingAmount!.amount && isAutoResaleEnabled) {
      waitingCheckAutoResaleMissingAmount();
    }
  }, [autoResaleMissingAmount, isAutoResaleEnabled]);

  const waitingCheckAutoResaleMissingAmount = (): void => {
    timeoutCheckAutoResaleMissingAmount.current = window.setTimeout(() => {
      mutateGetAutoResaleReturnsMissingAmount(undefined, {
        onSuccess: (data: TAutoResaleReturnsMissingAmountResponse) => {
          if (isAutoResaleEnabled && data.amount.amount > 0) {
            clearTimeout(timeoutCheckAutoResaleMissingAmount.current as number);
            setIsAutoResaleMissingAmount(data.amount);
          } else {
            waitingCheckAutoResaleMissingAmount();
          }
        }
      });
    }, 60000);
  };

  const waitingBalanceReplenished = (id: string): void => {
    if (account?.region === 'CN') {
      timeoutCheckBalance.current = window.setTimeout(() => {
        mutateCheckBalanceReplenishment(id, {
          onSuccess: (data: checkBalanceReplenishmentResponse) => {
            if (data.isProcess) {
              if (!balanceReplenishmentInProcessed) setBalanceReplenishmentInProcessed(true);
              waitingBalanceReplenished(id);
            } else {
              if (balanceReplenishmentInProcessed) setBalanceReplenishmentInProcessed(false);
              if (data.isSuccess) {
                clearTimeout(timeoutCheckQr.current as number);
                get();
                refetchAutoResaleMissingAmount();
                setIsAutoResaleMissingAmountValue(data.amount!);
                setIsOpenCompletionWindow(true);
                setIsOpenTopUpBalanceFormWindow(false);
              } else {
                if (data.errorCode) {
                  clearTimeout(timeoutCheckQr.current as number);
                  setBalanceReplenishmentError(data.errorCode);
                } else {
                  waitingBalanceReplenished(id);
                }
              }
            }
          }
        });
      }, 3000);
    }
    if (account?.region === 'TR') {
      if (!balanceReplenishmentInProcessed) setBalanceReplenishmentInProcessed(true);
      timeoutCheckBalance.current = window.setTimeout(() => {
        mutateCheckPaymentLinkStatus(id, {
          onSuccess: (data) => {
            if (data.isPaid) {
              if (balanceReplenishmentInProcessed) setBalanceReplenishmentInProcessed(false);
              clearTimeout(timeoutCheckLink.current as number);
              get();
              refetchAutoResaleMissingAmount();
              setIsAutoResaleMissingAmountValue(data.amount!);
              setIsOpenCompletionWindow(true);
              setIsOpenTopUpBalanceFormWindow(false);
            } else {
              waitingBalanceReplenished(id);
            }
          }
        });
      }, 3000);
    }
  };

  const setQrTimeout = (): void => {
    timeoutCheckQr.current = window.setTimeout(() => {
      setIsOpenTopUpBalanceFormWindow(false);
      clearTimeout(timeoutCheckBalance.current as number);
    }, 7200000);
  };

  const setLinkTimeout = (): void => {
    timeoutCheckLink.current = window.setTimeout(() => {
      setIsOpenTopUpBalanceFormWindow(false);
      clearTimeout(timeoutCheckBalance.current as number);
    }, 7200000);
  };

  const onCloseBalanceForm = (): void => {
    clearTimeout(timeoutCheckBalance.current as number);
    clearTimeout(timeoutCheckLink.current as number);
    clearTimeout(timeoutCheckQr.current as number);
    setIsOpenTopUpBalanceFormWindow(false);
  };

  const isAutoResaleSectionAvailable = account?.isTestUser && account?.features.customerReturnsResales && autoResaleMissingAmount;
  const isAutoresaleSortage = (isAutoResaleSectionAvailable && autoResaleMissingAmount!.amount > 0 && isAutoResaleEnabled);

  if (isLoadingAutoResaleData || isLoadingAutoResaleMissingAmount) {
    return (
      <Skeleton width="110px" height="36px" />
    )
  }

  return (
    <>
      <div className={styles.walletWidget}>
        <HoverCard
          disabled={!isAutoresaleSortage}
          Action={() => (
            <Button
              className={cn(styles.btn, isAutoresaleSortage && styles.shortage)}
              onClick={() => navigate('/profile?tab=finTab')}
              icon={WalletIcon}
              title={account?.balance ?? 0}
              size={ButtonSize.M}
              type={isAutoresaleSortage ? ButtonType.ATTENTION : ButtonType.SECONDARY}
            />
          )}
          Content={() => (
            <div className={styles.hoverCard}>
              <div className={styles.description}>
                <P1 color="error-sade">
                  {t(
                    'walletWidget.tooltip.title',
                    { amount: createSumFromObject(autoResaleMissingAmount!) }
                  )}
                </P1>
                <Title3 color="grey-800">
                  {t('walletWidget.tooltip.description')}
                </Title3>
              </div>
              <div className={styles.divider} />
              <div className={styles.footer}>
                <Button
                  size={ButtonSize.S}
                  type={ButtonType.SECONDARY}
                  onClick={() => navigate("/profile?tab=autoActionsTab")}
                  title={t('walletWidget.tooltip.actionLink')}
                />
                <Button
                  size={ButtonSize.S}
                  type={ButtonType.PRIMARY}
                  onClick={() => setIsOpenTopUpBalanceFormWindow(true)}
                  title={t('walletWidget.tooltip.actionBtn')}
                />
              </div>
            </div>
          )}
        />
      </div>
      {isAutoResaleSectionAvailable ? (
        <>
          <Modal
            isOpen={isOpenTopUpBalanceFormWindow}
            onClickOverlay={onCloseBalanceForm}
            onClose={onCloseBalanceForm}
            width={363}
          >
            <TopUpBalanceFormModalWindow
              balanceReplenishmentError={balanceReplenishmentError}
              balanceReplenishmentInProcessed={balanceReplenishmentInProcessed}
              handlerOnCloseBalanceForm={onCloseBalanceForm}
              handlerWaitingBalanceReplenished={waitingBalanceReplenished}
              handlerSetLinkTimeout={setLinkTimeout}
              handlerSetQrTimeout={setQrTimeout}
              replenishmentAmount={autoResaleMissingAmount!.amount * 2}
              subTitle={parse(t('walletWidget.topUpBalanceText')
                .replace('{{amount}}', createSumFromObject(autoResaleMissingAmount!))
                .replace('{{doubleAmount}}', createSumFromObject({
                  amount: autoResaleMissingAmount!.amount * 2,
                  currency: autoResaleMissingAmount!.currency
                })))
              }
            />
          </Modal>
          <Modal
            isOpen={isOpenCompletionWindow}
            onClickOverlay={() => setIsOpenCompletionWindow(false)}
            onClose={() => setIsOpenCompletionWindow(false)}
          >
            <CompletionModalWindow
              btnText={t('TopUpBalanceCompletedModalWindow.buttonFinish')}
              handlerSetIsOpenModalWindow={() => setIsOpenCompletionWindow(false)}
              title={t('TopUpBalanceCompletedModalWindow.info', { amount: autoResaleMissingAmountValue })}
            />
          </Modal>
        </>
      ) : null}
    </>
  );
};

export default WalletWidget;
